'use client';

import { useEffect, useState } from 'react';

import { DragEndEvent } from '@dnd-kit/core';

import { useProjectResponseModeContext } from '@/shared-survey';
import type { GetProjectQuery } from '@/survey-graphql';

import { seedRatingDefaultIfUnset } from './survey-ai-forms-ai-section-question.helpers';
import { handleMultiSelectToggle } from './survey-ai-forms.helpers';
import type { FormAnswerValue } from './survey-ai-forms.types';

type ProjectQuestion = NonNullable<
  GetProjectQuery['project']
>['questions'][number];

const EMPTY_OPTIONS: NonNullable<ProjectQuestion['options']> = [];

interface UseAiQuestionBaseProps {
  question: ProjectQuestion;
  value: FormAnswerValue;
  transcriptValue?: string;
  onSubmit?: () => void;
  onNext?: () => void;
  hasValue?: boolean;
}

interface UseAiRatingQuestionProps extends UseAiQuestionBaseProps {
  onChange: (value: string | number) => void;
}

interface UseAiOpenEndedQuestionProps extends UseAiQuestionBaseProps {
  onChange: (value: string) => void;
}

/**
 * Base hook for shared AI question logic
 * Handles isListening state, handleInputComposerSubmit, and canSubmit calculation
 */
function useAiQuestionBase({
  transcriptValue = '',
  hasValue = false,
  isOptional = false,
  onSubmit,
  onNext,
}: {
  transcriptValue?: string;
  hasValue?: boolean;
  isOptional?: boolean;
  onSubmit?: () => void;
  onNext?: () => void;
}) {
  // Get isListening from global context (like chat does)
  const { isListening } = useProjectResponseModeContext();

  // Handle input composer submit
  const handleInputComposerSubmit = () => {
    // If there's a transcript value, always call onNext to process it
    if (transcriptValue && transcriptValue.trim().length > 0) {
      onNext?.();
    } else if (hasValue) {
      // If no transcript but has form value, submit directly
      onSubmit?.();
    } else if (isOptional) {
      onNext?.();
    }
  };

  // Check if can submit
  const canSubmit =
    hasValue ||
    (transcriptValue ? transcriptValue.trim().length > 0 : false) ||
    isOptional;

  return {
    isListening,
    handleInputComposerSubmit,
    canSubmit,
  };
}

/**
 * Hook for AI rating question logic
 */
export function useAiRatingQuestion({
  question,
  value,
  transcriptValue = '',
  onChange,
  onSubmit,
  onNext,
  hasValue = false,
}: UseAiRatingQuestionProps) {
  const isOptional = question.required !== true;

  const { isListening, handleInputComposerSubmit, canSubmit } =
    useAiQuestionBase({
      transcriptValue,
      hasValue,
      isOptional,
      onSubmit,
      onNext,
    });

  const options = question.options ?? EMPTY_OPTIONS;

  // Use option indices as slider positions (0, 1, 2, 3, 4 for 5 options)
  const numberOfOptions = options.length || 5;
  const minIndex = 0;
  const maxIndex = numberOfOptions - 1;
  const stepSize = 0.01; // Smaller step for smoother movement

  // Parse current value to find its index
  let currentValueString: string | undefined;
  if (typeof value === 'string') {
    currentValueString = value;
  } else if (typeof value === 'number') {
    currentValueString = `${value}`;
  }
  const currentOptionIndex = currentValueString
    ? options.findIndex((opt) => opt.value === currentValueString)
    : undefined;

  // Default to middle option index (for visual display only)
  const defaultIndex = Math.floor(numberOfOptions / 2);

  // Track the current slider position for smooth movement (can be fractional)
  const [sliderPosition, setSliderPosition] = useState<number>(
    currentOptionIndex !== undefined && currentOptionIndex !== -1
      ? currentOptionIndex
      : defaultIndex
  );

  const [lastCurrentOptionIndex, setLastCurrentOptionIndex] =
    useState(currentOptionIndex);
  if (lastCurrentOptionIndex !== currentOptionIndex) {
    setLastCurrentOptionIndex(currentOptionIndex);
    if (currentOptionIndex !== undefined && currentOptionIndex !== -1) {
      setSliderPosition(currentOptionIndex);
    }
  }

  useEffect(
    () =>
      seedRatingDefaultIfUnset({
        value,
        options,
        numberOfOptions,
        currentOptionIndex,
        onChange,
      }),
    [value, options, numberOfOptions, currentOptionIndex, onChange]
  );

  const handleValueChange = (values: number[]) => {
    const newPosition = values[0];
    if (newPosition !== undefined) {
      // Update visual position immediately for smooth movement
      setSliderPosition(newPosition);
    }
  };

  const handleValueCommit = (values: number[]) => {
    const finalPosition = values[0];
    if (finalPosition !== undefined) {
      // Round to nearest integer index when user releases
      const roundedIndex = Math.round(finalPosition);

      // Ensure index is within bounds
      const validIndex = Math.max(minIndex, Math.min(maxIndex, roundedIndex));

      // Get the option at this index and use its value
      const selectedOption = options[validIndex];
      if (selectedOption) {
        // Send the actual option value as string (to match form section)
        onChange(selectedOption.value);
        // Snap to the exact position
        setSliderPosition(validIndex);
      }
    }
  };

  return {
    isListening,
    options,
    minIndex,
    maxIndex,
    stepSize,
    sliderPosition,
    canSubmit,
    handleValueChange,
    handleValueCommit,
    handleInputComposerSubmit,
  };
}

/**
 * Hook for AI open-ended question logic
 */
export function useAiOpenEndedQuestion({
  question,
  onSubmit,
  onNext,
  hasValue = false,
}: UseAiOpenEndedQuestionProps) {
  const isOptional = question.required !== true;

  // Use base hook for shared logic
  // For open-ended questions, we pass empty transcriptValue since it's handled differently
  const { isListening, canSubmit } = useAiQuestionBase({
    transcriptValue: '',
    hasValue,
    isOptional,
    onSubmit,
    onNext,
  });

  // Handle input composer submit for open-ended
  const handleInputComposerSubmit = () => {
    // For open-ended questions, just call onNext since the value is already in the form
    // The main AI section will handle saving the transcript if it came from voice
    if (hasValue || isOptional) {
      onNext?.();
    }
  };

  return {
    isListening,
    canSubmit,
    handleInputComposerSubmit,
  };
}

/**
 * Hook for AI select question logic (single and multi-select)
 */
export function useAiSelectQuestion({
  question,
  value,
  transcriptValue = '',
  onChange,
  onSubmit,
  onNext,
  hasValue = false,
}: UseAiQuestionBaseProps & {
  value: string | string[];
  onChange: (value: string | string[]) => void;
}) {
  const isOptional = question.required !== true;

  // Use base hook for shared logic
  const { isListening, handleInputComposerSubmit, canSubmit } =
    useAiQuestionBase({
      transcriptValue,
      hasValue,
      isOptional,
      onSubmit,
      onNext,
    });

  const options = question.options || [];
  const isMultiSelect = (question.maxSelections ?? 1) > 1;

  const isOptionSelected = (optionValue: string): boolean => {
    if (isMultiSelect) {
      return Array.isArray(value) && value.includes(optionValue);
    }
    return value === optionValue;
  };

  const handleOptionClick = (optionValue: string) => {
    if (isMultiSelect) {
      const currentValues = Array.isArray(value) ? value : [];
      const maxSelections = question.maxSelections ?? 1;
      const newValues = handleMultiSelectToggle(
        optionValue,
        currentValues,
        maxSelections
      );
      onChange(newValues);
    } else {
      const isAlreadySelected = value === optionValue;
      onChange(isAlreadySelected ? '' : optionValue);
    }
  };

  return {
    isListening,
    options,
    isMultiSelect,
    isOptionSelected,
    handleOptionClick,
    handleInputComposerSubmit,
    canSubmit,
  };
}

/**
 * Hook for AI ranking question logic
 */
export function useAiRankingQuestion({
  question,
  value,
  transcriptValue = '',
  onChange,
  onSubmit,
  onNext,
  hasValue = false,
}: Omit<UseAiQuestionBaseProps, 'value'> & {
  value: string[];
  onChange: (value: string[] | number | string) => void;
}) {
  const isOptional = question.required !== true;

  // Use base hook for shared logic
  const { isListening, handleInputComposerSubmit, canSubmit } =
    useAiQuestionBase({
      transcriptValue,
      hasValue,
      isOptional,
      onSubmit,
      onNext,
    });

  // Memoize options since it's used in other memoized values
  const options = question.options || [];

  // Get ranked options based on current value
  const rankedOptions =
    value?.map((v) => options.find((opt) => opt.value === v)).filter(Boolean) ||
    [];

  // Get available (unranked) options
  const availableOptions = options.filter((opt) => !value?.includes(opt.value));

  // Toggle option (add or remove from ranking)
  const toggleOption = (optionValue: string) => {
    const currentValues = Array.isArray(value) ? value : [];
    const newValues = currentValues.includes(optionValue)
      ? currentValues.filter((v) => v !== optionValue)
      : currentValues.concat(optionValue);
    onChange(newValues);
  };

  // Handle drag end
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || !value) return;

    const oldIndex = value.findIndex((item) => item === active.id);
    const newIndex = value.findIndex((item) => item === over.id);

    if (oldIndex !== newIndex && oldIndex !== -1 && newIndex !== -1) {
      const newValues = value.slice();
      const [movedItem] = newValues.splice(oldIndex, 1);
      newValues.splice(newIndex, 0, movedItem);
      onChange(newValues);
    }
  };

  return {
    isListening,
    options,
    rankedOptions,
    availableOptions,
    toggleOption,
    handleDragEnd,
    handleInputComposerSubmit,
    canSubmit,
  };
}

/**
 * Hook for AI image choice question logic
 */
export function useAiImageChoiceQuestion({
  question,
  value,
  transcriptValue = '',
  onChange,
  onSubmit,
  onNext,
  hasValue = false,
}: UseAiQuestionBaseProps & {
  value: string | string[];
  onChange: (value: string | string[]) => void;
}) {
  const isOptional = question.required !== true;

  // Use base hook for shared logic
  const { isListening, handleInputComposerSubmit, canSubmit } =
    useAiQuestionBase({
      transcriptValue,
      hasValue,
      isOptional,
      onSubmit,
      onNext,
    });

  // Get options with fallback
  const options = question.options || [];

  // Determine if multi-select
  const isMultiSelect = (question.maxSelections ?? 1) > 1;

  // Convert value to array for easier handling
  const selectedValues = (() => {
    if (Array.isArray(value)) return value;
    if (value) return [value];
    return [];
  })();

  // Handle option click with max selection enforcement
  const handleOptionClick = (optionValue: string) => {
    if (isMultiSelect) {
      // Multi-select: toggle option in array with max limit
      const maxSelections = question.maxSelections ?? 1;
      const newValues = handleMultiSelectToggle(
        optionValue,
        selectedValues,
        maxSelections
      );
      onChange(newValues);
    } else {
      // Single-select: toggle (allow deselection)
      const isAlreadySelected = selectedValues.includes(optionValue);
      onChange(isAlreadySelected ? '' : optionValue);
    }
  };

  return {
    isListening,
    options,
    isMultiSelect,
    selectedValues,
    handleOptionClick,
    handleInputComposerSubmit,
    canSubmit,
  };
}
